/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { JobContractDaysPeriodType } from '@gh/builder-shared-data';
import { FormState, FormStateBuilder, queryDirty, State, whenCreateMode } from '@gh/common-shared';
import { skip$ } from '@gh/rx/operators';
import { Subscription } from 'rxjs';
import {
  GANTT_INIT_STATE,
  ganttReducer,
  GanttState,
  GanttStateEventType,
  queryGanttLastUpdateTime,
} from '../../gantt-chart.state';
import { CalendarListItem, GanttTask } from '../../resources/gantt.model';
import { GanttChartBusService } from '../../services/gantt-chart-bus.service';
import { GanttChartCommandService } from '../../services/gantt-chart-command.service';
import { FormMode, WaitingContext, WaitingMode } from '@gh/core-ui';
import { WaitingProcess } from '../../../core-ui/waiting/waiting-process';
import { trim } from 'lodash';

export function ganttStateFactory(): State<GanttState> {
  return new State(GANTT_INIT_STATE, ganttReducer);
}

@Component({
  selector: 'gh-gantt-chart',
  template: require('./gantt-chart.component.html'),
  styles: [require('./gantt-chart.component.scss')],
  encapsulation: ViewEncapsulation.None, // tslint:disable-line:use-view-encapsulation
  providers: [
    GanttChartBusService,
    GanttChartCommandService,
    { provide: State, useFactory: ganttStateFactory },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'ghGanttChart',
})
export class GanttChartComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() editable: boolean;
  @Input() calendar: CalendarListItem[];
  @Input() multiMode: boolean = false;
  @Input() minDate: Date;
  @Input() currentDate: Date;
  @Input() scheduleTemplate?: any;
  @Input() periodType: JobContractDaysPeriodType = JobContractDaysPeriodType.WorkingDays;
  @Input() gDriveUrl?: string;
  @Input() initialRatio?: number;

  @Output() dirtyChange = new EventEmitter<boolean>();
  @Output() nameChanges = new EventEmitter<string>();
  @Output() sizeChange = new EventEmitter<string>();

  @Input()
  set tasks(tasks: GanttTask[]) {
    this._tasks = tasks;

    if (this.initialized) {
      this.state.mutate({
        type: GanttStateEventType.Merge,
        tasks: this._tasks,
      });
    }
  }

  get tasks(): GanttTask[] {
    return this._tasks;
  }
  form: FormState<any>;

  private timer: any;
  private waitingTimer: any;
  private waitingProcess: WaitingProcess | undefined;
  private initialized = false;
  private _tasks: GanttTask[];
  private dirtySubscription: Subscription;

  constructor(public api: GanttChartCommandService,
              private state: State<GanttState>,
              private formStateBuilder: FormStateBuilder,
              private waitingContext: WaitingContext,
              private detectChanges: ChangeDetectorRef,
              private bus: GanttChartBusService) {
  }

  ngOnInit(): void {
    this.state.mutate({
      type: GanttStateEventType.Load,
      tasks: this._tasks,
      calendar: this.calendar,
      editable: this.editable,
      multiMode: this.multiMode,
      periodType: this.periodType,
      minDate: this.minDate,
      currentDate: this.currentDate,
    });
    this.state.select(queryGanttLastUpdateTime).pipe(
      skip$(1)).subscribe((time) => {
      if (!this.waitingProcess) {
        this.waitingProcess = this.waitingContext.start(WaitingMode.Blocked);
      }
      clearTimeout(this.waitingTimer);
      // Debounce. Some changes in gantt can start cascade changes.
      // Add in the history the latest state after cascade changes
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.state.mutate({
                            type: GanttStateEventType.AddToHistory,
                          });
        this.waitingTimer = setTimeout(() => {
          if (this.waitingProcess) {
            this.waitingProcess.stop();
            this.waitingProcess = undefined;
          }
          // tslint:disable-next-line:no-magic-numbers
          },                           100);
        // tslint:disable-next-line:no-magic-numbers
      },                      200);
    });
    this.initialized = true;
    this.dirtySubscription = this.state.select(queryDirty).pipe(
      skip$(1))
      .subscribe((dirty) => this.dirtyChange.emit(dirty));
    this.form = this.formStateBuilder.buildPlain({
                                                   mode: FormMode.CreateMode,
                                                   enabled: whenCreateMode(),
                                                   value: {
                                                     name: this.scheduleTemplate ? this.scheduleTemplate.name : '',
                                                   },
                                                   children: {
                                                     name: { required: !!this.scheduleTemplate, enabled: this.editable} ,
                                                   },
                                                 });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.api.expandAllNotCompleted();
    });
  }

  ngOnDestroy(): void {
    this.dirtySubscription.unsubscribe();
  }

  onResizerPositionChange(filter: any): void {
    const debounce = 1000;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.sizeChange.emit(filter);
    },                      debounce);
    if (this.bus.initialized) {
      this.bus.resize();
    }
  }

  onExpandAll(): void {
    this.api.expandAll();
  }

  onCollapseAll(): void {
    this.api.collapseAll();
  }

  onSearch(event: string): void {
    const minLengthOfSearchString = 3;
    if (!event || event.length < minLengthOfSearchString) {
      if (!this.bus.searchString) {
        return;
      }
      this.bus.setFilter('');
      this.refreshTimeLine();
      return;
    }
    this.bus.setFilter(event);
    this.refreshTimeLine();
  }

  refreshTimeLine(): void {
    this.bus.gantt.refreshData();
    this.bus.gantt.render();
  }
  onNameChange(event: any): void {
    const value = trim(event.target.value);
    this.nameChanges.emit(value);
    this.form.getControl('name').setValue(value);
  }
}
